import { Icon } from '@iconify/react';
import React, { useState, useEffect, useCallback } from 'react';
import config from '../../config';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { Modal, Button } from 'react-bootstrap';
import InlineError from '../common/InlineError';

const PricingPlanMultipleColor = ({ onSubscribe }) => {
    const { user, auth } = useAuth();
    const [plans, setPlans] = useState([]);
    const [currentSub, setCurrentSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('monthly'); // 'monthly' or 'yearly'
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processing, setProcessing] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            // Fetch Plans
            const plansRes = await fetch(`${config.API_BASE_URL}/api/plans`);
            
            if (plansRes.ok) {
                const plansData = await plansRes.json();
                setPlans(plansData);
            } else {
                throw new Error('Failed to load subscription plans.');
            }

            // Fetch Current Subscription if logged in
            if (user) {
                const subRes = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
                if (subRes.ok) {
                    const subData = await subRes.json();
                    setCurrentSub(subData.subscription);
                }
            }

        } catch (err) {
            console.error('Error loading pricing data:', err);
            setError('Unable to load pricing options. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [user, auth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubscribeClick = (plan) => {
        if (!user) {
            toast.error('Please log in to subscribe.');
            return;
        }

        if (onSubscribe) {
            onSubscribe(plan);
            return;
        }

        if (currentSub) {
            if (currentSub.plan_id === plan.id) {
                return; // Already on this plan
            }
            setSelectedPlan(plan);
            setShowModal(true);
        } else {
            initiateCheckout(plan);
        }
    };

    const initiateCheckout = async (plan) => {
        setProcessing(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/create-checkout-session`, {
                method: 'POST',
                body: JSON.stringify({
                    planId: plan.id,
                    userId: user.id
                }),
            });

            const data = await response.json();

            if (data.url || data.approvalUrl) {
                window.location.href = data.url || data.approvalUrl;
            } else if (data.id) {
                 toast.error('Payment session created but redirect URL is missing.');
            } else {
                toast.error(data.error || 'Payment initialization failed.');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('An error occurred initializing payment.');
        } finally {
            setProcessing(false);
        }
    };

    const confirmChange = async () => {
        if (!selectedPlan) return;
        setProcessing(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/payments/change-plan`, {
                method: 'POST',
                body: JSON.stringify({
                    newPlanId: selectedPlan.id
                })
            });

            const data = await response.json();

            if (response.ok) {
                 if (data.url || data.approvalUrl) {
                     window.location.href = data.url || data.approvalUrl;
                     return;
                 }

                 if (data.action_required && data.client_secret) {
                     toast.info('Payment confirmation required. Redirecting...');
                     window.location.href = `/confirm-payment?secret=${data.client_secret}`;
                     return;
                 }

                 const subStatus = data.subscription?.status;
                if (subStatus === 'active' || subStatus === 'trialing') {
                    toast.success(`Successfully switched to ${selectedPlan.name}!`);
                    setShowModal(false);
                    const subRes = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
                    if (subRes.ok) {
                        const subData = await subRes.json();
                        setCurrentSub(subData.subscription);
                    }
                } else {
                    toast.warning(`Subscription updated but payment is ${subStatus}. Please check your payment method.`);
                    setShowModal(false);
                    const subRes = await auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`);
                    if (subRes.ok) {
                        const subData = await subRes.json();
                        setCurrentSub(subData.subscription);
                    }
                }
            } else {
                toast.error(data.error || 'Failed to update subscription.');
            }
        } catch (error) {
            console.error('Update error:', error);
            toast.error('An error occurred updating subscription.');
        } finally {
            setProcessing(false);
        }
    };

    const filteredPlans = plans.filter(plan => 
        (plan.duration_type || 'Monthly').toLowerCase() === activeTab.toLowerCase()
    );

    const getPlanTagline = (name = '') => {
        const lower = name.toLowerCase();
        if (lower.includes('free')) return 'Essential tools to get started';
        if (lower.includes('basic')) return 'Ideal for emerging venues & teams';
        if (lower.includes('premium')) return 'Advanced reach & multi-feature showcase';
        if (lower.includes('anniversary') || lower.includes('enterprise')) return 'Maximum scale with premium limits';
        return 'Elevate your venue on HubScroll';
    };

    const getColClass = (count) => {
        if (count === 4) return 'col-xxl-3 col-xl-3 col-md-6 col-12';
        if (count === 3) return 'col-xxl-4 col-xl-4 col-md-6 col-12';
        if (count === 2) return 'col-xxl-6 col-xl-6 col-md-6 col-12';
        return 'col-xxl-4 col-xl-4 col-md-6 col-12';
    };

    // Helper to parse features
    const getFeatures = (features) => {
        if (!features) return [];
        let parsedFeatures = features;
        
        if (typeof features === 'string') {
            try {
                parsedFeatures = JSON.parse(features);
            } catch (e) {
                return features.split(',').map(f => f.trim());
            }
        }

        if (Array.isArray(parsedFeatures)) return parsedFeatures;

        if (typeof parsedFeatures === 'object') {
            const displayList = [];
            if (parsedFeatures.galleryLimit) displayList.push(`Up to ${parsedFeatures.galleryLimit} Gallery Images`);
            if (parsedFeatures.blogs) displayList.push(`Up to ${parsedFeatures.blogs} Blog Posts`);
            if (parsedFeatures.rateOverview) displayList.push('Rate Overview & Analytics');
            if (parsedFeatures.premiumColor) displayList.push('Premium Profile Colors');
            return displayList;
        }

        return [];
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading pricing plans...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <InlineError 
                message={error}
                onRetry={fetchData}
            />
        );
    }

    return (
        <div className="hs-modern-card p-0">
            <div className="hs-modern-card-header">
                <div>
                    <h6 className="fw-bold mb-1 text-dark">Subscription Plans &amp; Quotas</h6>
                    <p className="hs-stat-helper mb-0">Select the plan that matches your business expansion scale</p>
                </div>
            </div>
            
            <div className="p-4 p-md-24">
                {/* Modern Segmented Billing Toggle */}
                <div className="d-flex justify-content-center mb-28">
                    <div className="hs-billing-toggle-pill shadow-xs">
                        <button 
                            type="button"
                            className={`hs-billing-btn fw-semibold ${activeTab === 'monthly' ? 'active' : ''}`}
                            onClick={() => setActiveTab('monthly')}
                        >
                            Monthly Billing
                        </button>
                        <button 
                            type="button"
                            className={`hs-billing-btn fw-semibold d-inline-flex align-items-center gap-2 ${activeTab === 'yearly' ? 'active' : ''}`}
                            onClick={() => setActiveTab('yearly')}
                        >
                            <span>Annual Billing</span>
                            <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle text-2xs fw-bold px-2 py-0.5">
                                Save 15%
                            </span>
                        </button>
                    </div>
                </div>

                <div className="row g-4 justify-content-center">
                    {filteredPlans.length === 0 ? (
                        <div className="hs-empty-state py-40">
                            <div className="hs-empty-icon">
                                <Icon icon="solar:card-2-bold" />
                            </div>
                            <h4 className="hs-empty-title">No plans available</h4>
                            <p className="hs-empty-desc">There are currently no active plans for this billing cycle.</p>
                        </div>
                    ) : (
                        filteredPlans.map((plan) => {
                            const isCurrent = currentSub && Number(currentSub.plan_id) === Number(plan.id);
                            const isPopular = plan.name?.toLowerCase().includes('premium');
                            const features = getFeatures(plan.features);

                            return (
                                <div key={plan.id} className={getColClass(filteredPlans.length)}>
                                    <div 
                                        className={`hs-pricing-card ${isCurrent ? 'is-current' : ''} ${!isCurrent && isPopular ? 'is-popular' : ''}`}
                                    >
                                        {/* Card Top / Header */}
                                        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                                            <div>
                                                <h4 
                                                    className="hs-plan-title mb-1" 
                                                    style={{ 
                                                        color: '#111827', 
                                                        fontSize: '18px', 
                                                        fontWeight: 800, 
                                                        textTransform: 'uppercase', 
                                                        letterSpacing: '0.5px',
                                                        lineHeight: '1.25',
                                                        margin: '0 0 4px 0'
                                                    }}
                                                >
                                                    {plan.name}
                                                </h4>
                                                <p className="text-xs mb-0" style={{ color: '#64748b' }}>
                                                    {getPlanTagline(plan.name)}
                                                </p>
                                            </div>
                                            {isCurrent ? (
                                                <span className="badge rounded-pill bg-primary text-white text-xs px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1 flex-shrink-0 shadow-xs">
                                                    <Icon icon="solar:check-circle-bold" className="text-sm" />
                                                    Active
                                                </span>
                                            ) : isPopular ? (
                                                <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-200 text-xs px-2 py-1 fw-semibold flex-shrink-0">
                                                    Popular
                                                </span>
                                            ) : null}
                                        </div>

                                        {/* Price Display */}
                                        <div className="my-16 pb-16 border-bottom" style={{ borderColor: 'var(--hs-border)' }}>
                                            <div className="d-flex align-items-baseline gap-1">
                                                <span className="text-xs fw-bold" style={{ color: '#64748b' }}>AED</span>
                                                <span className="text-3xl fw-bolder mb-0 lh-1" style={{ color: '#111827', fontWeight: 800 }}>
                                                    {parseFloat(plan.price) === 0 ? '0' : Number(plan.price).toLocaleString()}
                                                </span>
                                                <span className="text-xs fw-medium" style={{ color: '#64748b' }}>
                                                    /{plan.duration_type === 'yearly' ? 'year' : 'month'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Structured Core Quotas Box */}
                                        <div className="hs-plan-quota-box mb-16">
                                            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom border-neutral-200 border-opacity-50">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="w-24-px h-24-px rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0">
                                                        <Icon icon="solar:buildings-2-bold" className="text-xs" />
                                                    </div>
                                                    <span className="text-xs fw-medium" style={{ color: '#64748b' }}>Venues Allowed</span>
                                                </div>
                                                <strong className="text-xs fw-bold" style={{ color: '#111827' }}>{plan.max_venues}</strong>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="w-24-px h-24-px rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0">
                                                        <Icon icon="solar:stars-bold" className="text-xs" />
                                                    </div>
                                                    <span className="text-xs fw-medium" style={{ color: '#64748b' }}>Features / Venue</span>
                                                </div>
                                                <strong className="text-xs fw-bold" style={{ color: '#111827' }}>{plan.max_features}</strong>
                                            </div>
                                        </div>

                                        {/* Features Checklist */}
                                        <div className="mb-20 flex-grow-1">
                                            <span 
                                                className="text-2xs fw-bold text-uppercase letter-spacing-1 d-block mb-2"
                                                style={{ color: '#64748b', fontSize: '11px', letterSpacing: '0.8px' }}
                                            >
                                                Included Features
                                            </span>
                                            <ul className="d-flex flex-column gap-2 ps-0 mb-0 list-unstyled">
                                                {features.map((feature, i) => (
                                                    <li key={i} className="d-flex align-items-start gap-2 text-xs">
                                                        <Icon 
                                                            icon="solar:check-circle-bold" 
                                                            className="text-base flex-shrink-0 mt-0.5" 
                                                            style={{ color: '#16a34a' }}
                                                        />
                                                        <span style={{ color: '#334155', fontSize: '13px', lineHeight: '1.4' }}>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Action Button */}
                                        <div className="mt-auto pt-2">
                                            {isCurrent ? (
                                                <button 
                                                    type="button"
                                                    disabled
                                                    className="hs-btn-outline-primary w-100 radius-8 fw-semibold py-10 d-flex align-items-center justify-content-center gap-2 cursor-default"
                                                    style={{ backgroundColor: '#f0f9ff', borderColor: '#bae6fd', color: '#0789d5', opacity: 1 }}
                                                >
                                                    <span>Current Active Plan</span>
                                                </button>
                                            ) : (
                                                <button 
                                                    type="button"
                                                    onClick={() => handleSubscribeClick(plan)}
                                                    disabled={processing}
                                                    className="hs-btn-primary w-100 radius-8 fw-semibold py-10 d-flex align-items-center justify-content-center gap-2"
                                                >
                                                    <span>{currentSub ? 'Switch to this Plan' : 'Subscribe Now'}</span>
                                                    <Icon icon="solar:arrow-right-linear" className="text-sm" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton className="border-bottom py-3 px-4">
                    <div className="d-flex align-items-center gap-2">
                        <div className="w-36-px h-36-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                            <Icon icon="solar:card-transfer-bold" className="text-xl" />
                        </div>
                        <div>
                            <Modal.Title className="text-md fw-bold text-dark mb-0">Confirm Plan Upgrade</Modal.Title>
                            <span className="text-xs text-secondary-light">Adjust plan limits and quotas</span>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-sm mb-3">
                        Are you sure you want to switch to <strong>{selectedPlan?.name}</strong>?
                    </p>
                    <div className="p-3 border rounded-8 bg-neutral-50 mb-3">
                        <div className="text-xs text-secondary-light">
                            Note: Any price adjustments will be prorated against your current billing cycle.
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center p-3 bg-white border rounded-8">
                        <span className="text-sm text-secondary-light">New Rate:</span>
                        <strong className="text-dark">AED {selectedPlan?.price} / {selectedPlan?.duration_type === 'yearly' ? 'year' : 'month'}</strong>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-top py-2 px-4">
                    <button type="button" className="hs-btn-secondary" onClick={() => setShowModal(false)} disabled={processing}>
                        Cancel
                    </button>
                    <Button variant="primary" className="radius-8 fw-semibold" onClick={confirmChange} disabled={processing}>
                        {processing ? 'Processing...' : 'Confirm Switch'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PricingPlanMultipleColor;
